// ============================================
// 기존 aiTransaction 재사용 (AiManager에서 복사)
// ============================================

// enum raw value → Korean display label (intention-agnostic, reusable) //
const ENUM_KOREAN_MAP = {
  durationType: { SHORT_TERM: '단기', LONG_TERM: '장기' },
};

// items 배열의 enum field를 Korean label로 변환 (ENUM_KOREAN_MAP에 등록된 field만) //
function translateEnumFields(items) {
  if (!Array.isArray(items)) return items;

  for (const item of items) {
    for (const [field, labelMap] of Object.entries(ENUM_KOREAN_MAP)) {
      if (item[field] != null && labelMap[item[field]] != null) {
        item[field] = labelMap[item[field]];
      }
    }
  }

  return items;
}

// generic dedup helper — collapse rows with identical composite key values into the first occurrence.
//   - items: array of record objects
//   - keyFields: array of field names that uniquely identify a logical row from the user's perspective
//     (e.g. for product catalog: ['productName','price'] — same product variant from user perspective;
//      for biz send: ['sendType','title','createdDate'] — same send event with multiple recipient rows)
//   - dropFields (optional): array of internal/recipient-level field names to strip from the surviving row
//     (so the row no longer carries variant-specific metadata that caused the original duplication)
//
// Usage pattern: backend responses that pre-flatten 1-N relations (1 send × N recipients = N rows, or
// 1 product display × N SKU variants = N rows) produce visually identical rows in user-facing tables
// because the display columns hide the varying internal field. This helper restores the 1-row-per-logical-unit
// semantics generically by intention-meaningful field composition (no value match, no case-specific string).
function dedupByKey(items, keyFields, dropFields = []) {
  if (!Array.isArray(items) || items.length === 0) return items;
  if (!Array.isArray(keyFields) || keyFields.length === 0) return items;

  const seen = new Set();
  const unique = [];

  for (const it of items) {
    if (!it || typeof it !== 'object') {
      unique.push(it);
      continue;
    }

    const keyParts = keyFields.map(f => {
      const v = it[f];
      // distinguish null vs undefined vs '' to avoid false collapse
      if (v === null) return '\0NULL';
      if (v === undefined) return '\0UNDEF';
      return String(v);
    });
    const key = keyParts.join('');

    if (seen.has(key)) continue;
    seen.add(key);

    if (dropFields.length > 0) {
      const cleaned = { ...it };
      for (const f of dropFields) delete cleaned[f];
      unique.push(cleaned);
    }
    else {
      unique.push(it);
    }
  }

  return unique;
}

// parseResponse 예외: 비즈니스 로직이 필요한 인텐션만 유지 //
export const PARSE_RESPONSE_OVERRIDES = {
  // 만료 예정 회원 — 가장 임박한 회원권 1건 선택 + "외 N건" 합성 //
  'CHECK_MEMBER+EXPIRING_LIST_BY+PERIOD': (data) => {
    if (!data?.members) return data;
    return data.members.map(member => {
      const { expiringMemberships, ...parentFields } = member;
      const memberships = expiringMemberships || [];
      const nearest = memberships.reduce((a, b) =>
        (a.remainingDays ?? Infinity) <= (b.remainingDays ?? Infinity) ? a : b
      , memberships[0] || {});
      return {
        ...parentFields,
        memberShipName: memberships.length > 1
          ? `${nearest.memberShipName} 외 ${memberships.length - 1}건`
          : nearest.memberShipName,
        startDate: nearest.startDate,
        endDate: nearest.endDate,
        remainingDays: nearest.remainingDays,
      };
    });
  },
  // 제휴 센터 목록 — raw PK(partnerId, followStoreId, storeId) + 이미지 URL 제거, 전화번호 포맷 //
  'CHECK_PARTNER+GYM_LIST': (data) => {
    if (!data) return data;

    // format phone number: 01012345678 -> 010-1234-5678 //
    function formatTel(tel) {
      if (!tel || typeof tel !== 'string') return tel;
      const digits = tel.replace(/\D/g, '');

      if (digits.length === 11) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
      }
      else if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }

      return tel;
    }

    // clean raw item — keep only user-facing fields //
    function cleanItem(p) {
      return {
        storeName: p.storeName || '',
        storeTel: formatTel(p.storeTel) || '',
        storeAddress: p.storeAddress || '',
        openingHour: p.openingHour || '',
        ...(p.partnerType ? { partnerType: p.partnerType } : {}),
      };
    }

    // backend response variants:
    //   { attendancePartners: [...], exerciseSharePartners: [...] }
    //   { follows: [...], followers: [...] }
    //   flat array
    let items = [];

    if (Array.isArray(data)) {
      items = data;
    }
    else if (typeof data === 'object') {
      const attendance = data.attendancePartners || [];
      const exercise = data.exerciseSharePartners || [];
      const follows = data.follows || [];
      const followers = data.followers || [];

      if (attendance.length > 0 || exercise.length > 0) {
        for (const p of attendance) {
          items.push({ ...p, partnerType: '출석 제휴' });
        }

        for (const p of exercise) {
          items.push({ ...p, partnerType: '운동 공유' });
        }
      }
      else if (follows.length > 0 || followers.length > 0) {
        for (const p of follows) {
          items.push(p);
        }

        for (const p of followers) {
          items.push(p);
        }
      }
      else {
        // fallback: find first object-array property
        for (const key of Object.keys(data)) {
          if (Array.isArray(data[key]) && data[key].length > 0 && typeof data[key][0] === 'object') {
            items = data[key];
            break;
          }
        }
      }
    }

    if (items.length === 0) return data;

    // deduplicate by storeName — follows/followers can overlap //
    const seen = new Set();
    const unique = [];

    for (const p of items) {
      const key = p.storeName || '';

      if (!seen.has(key)) {
        seen.add(key);
        unique.push(p);
      }
    }

    const cleaned = unique.map(cleanItem);

    // return flat array — extractArray will pick it up directly
    return cleaned;
  },
  // 수업별 출석 통계 — timeSlots 배열 → 개수 + join 파생 //
  'CHECK_RESERVE+ATTENDANCE_STATISTICS_BY+LESSON': (data) => {
    if (!data?.lessons) return data;
    return data.lessons.map(lesson => {
      const { timeSlots, ...fields } = lesson;
      return {
        ...fields,
        timeSlotCount: timeSlots?.length || 0,
        classTime: (timeSlots || []).map(s => s.classTime).join(', '),
      };
    });
  },
  // WOD 목록 — sections[] 배열 평탄화: customTitle + mainProgram 결합 → wodContent 필드 (Cat5-INFO_LOSS fix) //
  // backend response: { storeId, storeName, contents: [{ workoutId, title, sections: [...] }] }
  'CHECK_WORKOUT+WOD_LIST_BY+PERIOD': (data) => {
    const items = Array.isArray(data) ? data : (data?.contents || data?.workouts);
    if (!items) return data;

    const flattened = items.map(wod => {
      const { sections, ...fields } = wod;
      if (!Array.isArray(sections) || sections.length === 0) {
        return { ...fields, wodContent: '' };
      }

      const contentParts = [];

      for (const sec of sections) {
        const title = (sec.customTitle || sec.tag || '').trim();
        const program = (sec.mainProgram || '').trim();
        if (title && program) {
          contentParts.push(`[${title}] ${program}`);
        }
        else if (program) {
          contentParts.push(program);
        }
        else if (title) {
          contentParts.push(`[${title}]`);
        }
      }

      return { ...fields, wodContent: contentParts.join(' / ') };
    });

    if (Array.isArray(data)) return flattened;
    if (data?.contents) return { ...data, contents: flattened };
    return { ...data, workouts: flattened };
  },
  // WOD 참가자 — workouts[].participants[] 1:N unnesting (참가자 회원 단위 row) //
  // linkCoachId→userId 매핑은 aiFieldConfig.js FIELD_REGISTRY normalize에서 자동 처리
  'CHECK_WORKOUT+PARTICIPANT_LIST_BY+PERIOD': (data) => {
    if (!data?.workouts) return data;
    const flattened = [];
    for (const workout of data.workouts) {
      const { participants, ...workoutFields } = workout;
      if (!participants || participants.length === 0) continue;
      for (const p of participants) {
        flattened.push({ ...workoutFields, ...p });
      }
    }
    return { ...data, workouts: flattened };
  },
  // 매출 거래 내역 — isRefund sparse field 정규화: 환불 row만 isRefund=true, 나머지는 field 부재 → false 보정 //
  // COLUMN GUARD가 items[0] keys 기준 검증하므로 sparse field는 show_display에서 rejected (L9b-CROSS-012 Cat5-INFO_LOSS)
  'CHECK_SALES_STATISTICS_BY+PERIOD': (data) => {
    if (!data) return data;

    const items = Array.isArray(data) ? data : (data?.sales || data?.items);
    if (!Array.isArray(items)) return data;

    for (const item of items) {
      if (item.isRefund === undefined || item.isRefund === null) {
        item.isRefund = false;
      }
    }

    return data;
  },
  // 연간 매출 통계 — isRefund sparse field 정규화 (CHECK_SALES_STATISTICS_BY+PERIOD와 동일 패턴) //
  'CHECK_SALES+YEAR_STATISTICS': (data) => {
    if (!data) return data;

    const items = Array.isArray(data) ? data : (data?.sales || data?.items);
    if (!Array.isArray(items)) return data;

    for (const item of items) {
      if (item.isRefund === undefined || item.isRefund === null) {
        item.isRefund = false;
      }
    }

    return data;
  },
  // 회원권 상세 — backend status 미갱신 보정 + 식별 라벨 합성 (mixed-form 대응)
  // (1) status 보정: endDate < today && status=1 → status=2(만료) — backend가 기간 만료 후에도 status=1(활성)을 유지하는 case 존재 (드랍인 등) — 프론트에서 결정적 보정
  // (2) [L1-MEMBER_DETAIL-007 RESIDUAL] mixed-form items 식별 라벨 합성:
  //     - intention metadata 정의대로 두 form 혼재 가능 — form (A) 본체 row(memberShipName 보유) + form (B) 일시정지/연장 row(holdingId 보유, memberShipName 부재).
  //     - aceae639가 cols에서 memberShipName drop은 막았지만, 일시정지 row는 실제 값이 부재해서 cell이 비어 보임 → 사용자가 "이 row가 뭐지" 식별 불가.
  //     - 구조 기반 합성: holdingId 보유 + memberShipName 부재 → memberShipName="[일시정지]" 합성 (intention metadata "회원권 일시정지 내역" naming convention 정합).
  //     - 도메인/언어 무관: row의 type-marker field(holdingId)는 backend convention. 라벨 한국어는 intention metadata가 이미 한국어("회원권 일시정지")로 정의되어 있어 정합.
  //     - 대상 array: backend response의 모든 object array 순회 — { memberShipDetails: [...], memberShipHistoryDates: [...] } 양쪽 또는 flat array 모두 처리 (extractArray가 어느 키를 select해도 일관성 확보).
  'CHECK_MEMBER+MEMBERSHIP_DETAIL': (data) => {
    if (!data) return data;

    // backend response: { memberShipDetails: [...], memberShipHistoryDates: [...] } 또는 flat array
    // structure-agnostic: 모든 object array를 순회 (extractArray가 어떤 key를 pick해도 정합 유지)
    const itemArrays = [];
    if (Array.isArray(data)) {
      itemArrays.push(data);
    } else if (typeof data === 'object') {
      for (const k of Object.keys(data)) {
        if (Array.isArray(data[k]) && data[k].length > 0 && typeof data[k][0] === 'object') {
          itemArrays.push(data[k]);
        }
      }
    }
    if (itemArrays.length === 0) return data;

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    for (const items of itemArrays) {
      for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        // (1) status 보정
        if (item.status === 1 && item.endDate) {
          const end = new Date(item.endDate);
          if (!isNaN(end.getTime()) && end < now) {
            item.status = 2;
          }
        }
        // (2) HOLDING-row identity label 합성 — holdingId 보유 + memberShipName 부재 시 합성
        if (item.holdingId != null && (item.memberShipName == null || item.memberShipName === '')) {
          item.memberShipName = '[일시정지]';
        }
      }
    }

    return data;
  },
  // 락커 상세 — backend response: { lockerName, currentState (Empty/InUse), totalUsageCount, histories[] } //
  // histories 비어있어도 락커 자체는 존재 — 1-row 메타로 변환해서 "0건 = 미존재" 오해석 차단 (1-M dual sync) //
  'CHECK_LOCKER_DETAIL': (data) => {
    if (!data || typeof data !== 'object' || !data.lockerName) return data;
    const STATE_LABEL = { 'Empty': '비어있음', 'InUse': '사용중' };
    const histories = Array.isArray(data.histories) ? data.histories : [];
    if (histories.length > 0) {
      return histories.map(h => ({
        ...h,
        lockerName: data.lockerName,
        currentState: STATE_LABEL[data.currentState] || data.currentState,
      }));
    }
    return [{
      lockerName: data.lockerName,
      currentState: STATE_LABEL[data.currentState] || data.currentState,
      totalUsageCount: data.totalUsageCount,
    }];
  },
  // 가입 후 경과월수별 평균 출석률 추이 — peak/dropOff 시점 정합 보정 (SCENARIO-5-LIFECYCLE) //
  //   backend 알고리즘 quirk: tenureMonth asc 순회로 첫 미달 시점을 dropOff로 잡음 → 0개월 차(가입 첫 달) 표본이 작아 임계값 이하면 dropOff=0
  //   이로 인해 peakRetentionMonth=11 + dropOffStartMonth=0 같은 self-contradiction (피크 이후 이탈 시점이 피크보다 앞) 발생
  //   본 transform 책임:
  //     (1) dropOff < peak 인 경우 dropOff 재계산 (peak 이후 items에서만 탐색). peak 이후 미달 없으면 null 처리.
  //     (2) backend Jackson NON_NULL 직렬화로 null 키가 응답에서 누락 → LLM이 키 부재를 peak 값으로 fabricate 차단 위해 null 키 명시 주입.
  //     (3) threshold pair 명시 — 매칭 안되면 threshold key가 없어도 진행 (null 보정만)
  //   생애주기/cohort 자기모순 차단 — 모든 dropOffStartMonth* 키 (relative/absolute/추후 확장) 공통 적용 (필드명 prefix 패턴 기반).
  'CHECK_MEMBER+ATTENDANCE+TENURE+TREND_STATISTICS_BY+PERIOD': (data) => {
    if (!data || typeof data !== 'object') return data;
    const summary = data.summary && typeof data.summary === 'object' ? data.summary : null;
    const items = Array.isArray(data.items) ? data.items : null;
    if (!summary) return data;

    const peakMonth = summary.peakRetentionMonth;
    const peakValid = (peakMonth != null && typeof peakMonth === 'number');

    // tenureMonth asc 정렬 보장 (backend convention)
    const sortedItems = items
      ? items.slice().sort((a, b) => (a?.tenureMonth ?? 0) - (b?.tenureMonth ?? 0))
      : [];

    // threshold key → 대응 dropOff start key — backend convention 'dropOffThresholdX' ↔ 'dropOffStartMonthX'
    // 이를 통해 backend가 null 직렬화 omit한 dropOffStartMonth* 키도 null로 명시 주입
    const dropOffThresholdKeys = Object.keys(summary).filter(k => k.startsWith('dropOffThreshold'));
    const expectedDropOffKeys = dropOffThresholdKeys.map(k => k.replace('Threshold', 'StartMonth'));

    // 누락된 dropOffStartMonth* 키 null로 명시 주입 (backend NON_NULL omit 대응)
    for (const k of expectedDropOffKeys) {
      if (!(k in summary)) summary[k] = null;
    }

    if (!peakValid) return data;  // peak 없으면 보정 불가, 키만 주입하고 종료

    // dropOff* 재계산: 현재 dropOff가 < peak 인 경우 peak 이후 items에서 첫 미달 재탐색
    for (const key of expectedDropOffKeys) {
      const currentDropOff = summary[key];
      if (currentDropOff == null) continue;  // 이미 null — 보정 불필요
      if (typeof currentDropOff !== 'number') continue;
      if (currentDropOff >= peakMonth) continue;  // 정상 — dropOff가 peak 이후

      // contradiction: dropOff < peak. peak 이후 items에서 첫 미달 시점 재탐색
      const thresholdKey = key.replace('StartMonth', 'Threshold');
      const threshold = summary[thresholdKey];
      if (typeof threshold !== 'number') continue;

      let recomputed = null;
      for (const item of sortedItems) {
        if (typeof item?.tenureMonth !== 'number') continue;
        if (item.tenureMonth <= peakMonth) continue;  // peak 이후만 (peak 자체 제외 — peak는 최고점)
        if (typeof item?.avgAttendanceRate !== 'number') continue;
        if (item.avgAttendanceRate <= threshold) {
          recomputed = item.tenureMonth;
          break;
        }
      }
      summary[key] = recomputed;  // peak 이후 미달 없으면 null
    }

    data.summary = summary;
    return data;
  },
  // 장기/단기 회원권 비율 — durationType raw enum(SHORT_TERM/LONG_TERM) 한국어 변환 //
  'CHECK_MEMBERSHIP+TERM+RATIO_STATISTICS_BY+PERIOD': (data) => {
    if (!data) return data;

    if (Array.isArray(data)) {
      translateEnumFields(data);
      return data;
    }

    // structure-agnostic: find the largest object array in response //
    for (const key of Object.keys(data)) {
      if (Array.isArray(data[key]) && data[key].length > 0 && typeof data[key][0] === 'object') {
        translateEnumFields(data[key]);
      }
    }

    return data;
  },
  // 결제수단별 비율 통계 — backend summary의 totalTransactionCount/totalAmount가 환불·취소 포함 raw 집계라
  //   사용자가 보는 표(완료 거래만 row 집계)의 row-sum과 mismatch 발생 (e.g. summary 43 vs row-sum 38). LLM이
  //   intro placeholder를 {{SUMMARY:totalTransactionCount}}로 선택 시 표와 자기모순.
  //   본 transform 책임: items 존재 시 summary.totalTransactionCount/totalAmount를 row-sum으로 결정적 덮어쓰기.
  //   효과:
  //     (1) tool_result message 백엔드 메타지표 totalTransactionCount=<row-sum> 노출 → LLM 정보원 일치
  //     (2) {{SUMMARY:totalTransactionCount}} placeholder substitute 시 row-sum 반환 — {{SUM:transactionCount}}와 등가
  //     (3) placeholder choice stochastic 제거 → self-contradiction 결정적 차단 (#762 SYN-CHECK_SALES+PAYMENT+RATIO_STATISTICS_BY+PERIOD-3)
  //   범용성: 단일 intention scope이지만 hardcode 수치 X — items 구조(필드 존재 + 숫자 합) 기반.
  'CHECK_SALES+PAYMENT+RATIO_STATISTICS_BY+PERIOD': (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return data;

    // structure-agnostic items 추출: 본 intention 응답은 결제수단별 row 배열 + flat summary scalar 혼합
    //   backend 변형 대응: { items, summary, ... } 또는 { payments, ... } 등 largest object array
    let items = null;
    if (Array.isArray(data.items)) items = data.items;
    if (!items) {
      for (const key of Object.keys(data)) {
        const arr = data[key];
        if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object') {
          if (!items || arr.length > items.length) items = arr;
        }
      }
    }
    if (!Array.isArray(items) || items.length === 0) return data;

    // summary 보존 (없으면 새로 생성 — flat scalar는 extractArray에서 평탄화 시 summary 합류)
    const hasNested = data.summary && typeof data.summary === 'object' && !Array.isArray(data.summary);
    const target = hasNested ? data.summary : data;

    // row-sum 계산 — 필드 부재 시 skip (구조 기반, 다른 row schema의 RATIO_STATISTICS 변형은 영향 X)
    const sumField = (field) => {
      let s = 0, hit = 0;
      for (const it of items) {
        const v = it && typeof it === 'object' ? it[field] : null;
        if (typeof v === 'number' && isFinite(v)) { s += v; hit++; }
      }
      return hit > 0 ? s : null;
    };

    const rowSumTransaction = sumField('transactionCount');
    if (rowSumTransaction !== null) {
      target.totalTransactionCount = rowSumTransaction;
    }

    const rowSumAmount = sumField('totalAmount');
    if (rowSumAmount !== null) {
      target.totalAmount = rowSumAmount;
    }

    return data;
  },
  // 상품 카탈로그 — backend가 동일 상품의 SKU variant를 별도 row로 직렬화 (productId만 다르고 productName/price/inventory 동일)
  //   → user-facing 표는 productId/picture를 숨기므로 시각적으로 N건 동일 row dup. catalog 의미상 1 row per (productName+price).
  //   structural dedup: (productName + price) 동일 → 1 row. inventory/picture/productId 등 hidden 필드는 drop.
  'CHECK_PRODUCT+CATALOG_LIST': (data) => {
    const items = Array.isArray(data) ? data : (data?.products || data?.items);
    if (!Array.isArray(items) || items.length === 0) return data;

    const deduped = dedupByKey(items, ['productName', 'price'], ['productId', 'picture']);

    if (Array.isArray(data)) return deduped;
    if (data?.products) return { ...data, products: deduped };
    if (data?.items) return { ...data, items: deduped };
    return deduped;
  },
  // 비즈 알림 발송 내역 — backend가 (1 send) × (N recipients)을 N row로 평탄화 (sendType/title/createdDate 동일 + memberId/name/phone만 변동)
  //   또는 (1 send + nested members[]) 형태로 응답 — autoFlatten이 members[]를 unnest하면서 동일 send가 N번 반복.
  //   → user-facing 표는 recipient 필드를 숨기므로 시각적으로 동일 send가 N번 반복. "발송 내역" 의미상 1 row per 발송 event.
  //   structural dedup: (sendType + title + createdDate) 동일 → 1 row. 그리고 recipient-level 필드/nested 배열 drop
  //   하여 autoFlatten의 추가 unnest 차단. send-event 단위 표시 유지.
  //   backend response 구조 다양 (flat array / 키 미상 nested) 대응 위해 structure-agnostic largest-object-array 탐색.
  'CHECK_BIZ_LIST_BY+PERIOD': (data) => {
    if (!data) return data;

    // structure-agnostic items 추출 (largest object array)
    let items = null;
    let arrKey = null;
    if (Array.isArray(data)) {
      items = data;
    }
    else if (typeof data === 'object') {
      let bestLen = 0;
      for (const k of Object.keys(data)) {
        const arr = data[k];
        if (Array.isArray(arr) && arr.length > bestLen && (arr.length === 0 || typeof arr[0] === 'object')) {
          items = arr;
          arrKey = k;
          bestLen = arr.length;
        }
      }
    }

    if (!Array.isArray(items) || items.length === 0) return data;

    // recipient-level 필드 + nested members[] 배열 drop (autoFlatten의 추가 unnest 차단)
    const recipientFields = ['memberId', 'userId', 'name', 'phone', 'countryCode', 'profile', 'members'];
    const deduped = dedupByKey(items, ['sendType', 'title', 'createdDate'], recipientFields);

    if (Array.isArray(data)) return deduped;
    if (arrKey) return { ...data, [arrKey]: deduped };
    return deduped;
  },
};

// ============================================
// STATUS_FILTER_MAP — ternary/multi-valued status enum 자동 filter
//
// Why: backend가 status mixed로 반환(e.g. APPLICATION_LIST: approval 0/1/2 동시).
//      사용자 input이 status keyword 포함 시 LLM이 filter 누락하거나 잘못된
//      filter 적용해 사용자 입장 self-contradiction 발생 ("대기" 물었는데
//      "5명 거절" 표시 / "대기 없어" hallucination 등).
//
// Generic 메커니즘: intentionKey → field + keywordToValue map.
//      AgentSession + nodeAgent에서 post-callApi hook으로 user input scan,
//      match 시 items pre-filter (>0) 또는 emptyStatusContext 기록 (=0).
//
// 추가 intention: 동일 schema entry 추가하면 됨 (generic — 단일 case hardcode X).
// ============================================
export const STATUS_FILTER_MAP = {
  'CHECK_MEMBERSHIP+APPLICATION_LIST': {
    field: 'approval',
    statusValues: {
      pending: 0,
      approved: 1,
      rejected: 2,
    },
    // RegExp pattern → status key.
    // 매칭 우선순위: 첫 매칭 우선 (긴 keyword 먼저 배치 권장).
    keywordPatterns: [
      // pending = 0 (승인 대기 / 미승인 / 대기 회원)
      { pattern: /승인\s*대기|대기\s*회원|대기\s*명단|대기\s*중인|미승인|승인\s*안\s*된|승인\s*안된|승인\s*안한|승인\s*전|신청\s*대기/, statusKey: 'pending' },
      // rejected = 2 (거절 / 반려)
      { pattern: /거절(?:된|한|되었|돼)|반려(?:된|한|되었|돼)|거절\s*명단|반려\s*명단|거절\s*회원|반려\s*회원/, statusKey: 'rejected' },
      // approved = 1 (승인 완료 / 가입 승인 — "승인 대기" 우선이라 위에서 매칭 X)
      { pattern: /승인된|승인\s*완료|가입\s*승인|승인\s*받은|승인\s*회원|승인\s*명단/, statusKey: 'approved' },
      // "대기" 단독 (fallback for "대기" without "승인" prefix — 대기 + 회원/명단/리스트/보여/알려 신호와 결합 시)
      { pattern: /(?:^|\s)대기(?:\s|$)/, statusKey: 'pending' },
    ],
    // 매칭 후 honest 응답에 사용할 라벨 (intro/finish 자연어용)
    statusLabels: {
      pending: '승인 대기',
      approved: '승인 완료',
      rejected: '거절',
    },
    // 응답에 사용할 명사 ("X 신청은 없어")
    nounLabel: '신청',
  },
};

// "GET /api2/member/detail/{member_id}" → { method, apiVersion, path } //
export function parseEndpoint(endpointStr) {
  if (!endpointStr) return null;

  const spaceIdx = endpointStr.indexOf(' ');
  const method = endpointStr.slice(0, spaceIdx);
  const rawPath = endpointStr.slice(spaceIdx + 1);
  const versionMatch = rawPath.match(/^\/(api2?)\/(.*)/);

  if (!versionMatch) return null;

  return { method, apiVersion: versionMatch[1], path: versionMatch[2] };
}

// service + apiVersion + method → FetchModule 메서드명 //
export function resolveFetchMethod(service, apiVersion, method) {
  if (service === 'exercise') {
    return method === 'POST' ? 'postDataOfExerciseVersion2' : 'getDataOfExerciseVersion2';
  }

  if (apiVersion === 'api') {
    if (method === 'POST') return 'postData';
    return 'getIndividualData';
  }

  return method === 'POST' ? 'postDataVersion2' : 'getDataVersion2';
}

// endpoint path + params 스키마 → 최종 URL 생성 //
export function buildDynamicEndpoint(path, paramsSchema, paramValues) {
  const pathParamNames = [...path.matchAll(/\{(\w+)\}/g)].map(m => m[1]);

  let resolvedPath = path.replace(/\{(\w+)\}/g, (_, key) => {
    const camelKey = key.replace(/_(\w)/g, (__, c) => c.toUpperCase());
    return paramValues[camelKey] || paramValues[key] || '';
  });

  const queryKeys = (paramsSchema || [])
    .filter(p => !pathParamNames.includes(p.name) && !p.fixed)
    .map(p => p.name);
  if (queryKeys.length > 0) {
    const queryParams = new URLSearchParams();

    for (const key of queryKeys) {
      const camelKey = key.replace(/_(\w)/g, (_, c) => c.toUpperCase());
      const value = paramValues[camelKey] || paramValues[key];
      if (value) queryParams.append(key, value);
    }

    const query = queryParams.toString();
    if (query) resolvedPath = `${resolvedPath}?${query}`;
  }

  return resolvedPath;
}

// (P2c) buildAiLogRequest 제거 — 구 ai-log 개별 POST 폐기, 시임1 콜렉터(aiLlmCollector)가 /turn llmCalls[]로 대체 //
